import { useEffect, useRef, useSyncExternalStore } from 'react';
import { api, ApiError } from './api';
import { loadCoachChatHistory, saveCoachChatHistory } from './coachChatHistory';
import { localDateKey } from './dateKey';

export type ChatTurn = { role: 'user' | 'assistant'; content: string };

type Snapshot = { turns: ChatTurn[]; input: string; busy: boolean };

let turns: ChatTurn[] = [];
let input = '';
let busy = false;
let sessionUserId: string | undefined;
const listeners = new Set<() => void>();

/** Must be referentially stable between emits or useSyncExternalStore loops forever (React #185). */
let snapshot: Snapshot = { turns, input, busy };

function refreshSnapshot(): void {
  snapshot = { turns, input, busy };
}

function emit() {
  refreshSnapshot();
  listeners.forEach((l) => l());
}

export function subscribeCoachChat(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
}

export function getCoachChatSnapshot(): Snapshot {
  return snapshot;
}

export function isCoachChatBusy(): boolean {
  return busy;
}

export function ensureCoachChatSession(userId?: string) {
  if (sessionUserId !== userId) {
    sessionUserId = userId;
    if (!busy) {
      turns = loadCoachChatHistory(userId);
      input = '';
    }
    emit();
  }
}

export function setCoachChatTurns(next: ChatTurn[] | ((prev: ChatTurn[]) => ChatTurn[])) {
  turns = typeof next === 'function' ? next(turns) : next;
  saveCoachChatHistory(sessionUserId, turns);
  emit();
}

export function setCoachChatInput(value: string) {
  input = value;
  emit();
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError && e.body) {
    try {
      const j = JSON.parse(e.body) as { error?: string };
      if (j.error) return j.error;
    } catch {
      /* ignore */
    }
  }
  return e instanceof Error ? e.message : 'Something went wrong.';
}

/** Sends a coach message; completes even if the Coach tab unmounted (background thinking). */
export async function sendCoachChatMessage(textOverride?: string, priorTurns?: ChatTurn[]): Promise<boolean> {
  const text = (textOverride ?? input).trim();
  if (!text || busy) return false;

  input = '';
  const base = priorTurns ?? turns;
  const nextTurns: ChatTurn[] = [...base, { role: 'user', content: text }];
  turns = nextTurns;
  busy = true;
  emit();

  try {
    const res = await api<{ reply: string }>('/api/ai/coach-chat', {
      method: 'POST',
      body: JSON.stringify({
        messages: nextTurns,
        tzOffset: new Date().getTimezoneOffset(),
        dateKey: localDateKey(),
      }),
    });
    turns = [...nextTurns, { role: 'assistant', content: res.reply }];
    return true;
  } catch (e) {
    turns = [...nextTurns, { role: 'assistant', content: errMsg(e) }];
    return false;
  } finally {
    busy = false;
    saveCoachChatHistory(sessionUserId, turns);
    emit();
  }
}

export function useCoachChatSession(userId?: string) {
  const state = useSyncExternalStore(subscribeCoachChat, getCoachChatSnapshot, getCoachChatSnapshot);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ensureCoachChatSession(userId);
  }, [userId]);

  useEffect(() => {
    if (!state.busy && state.turns.length) {
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
    }
  }, [state.busy, state.turns.length]);

  return {
    turns: state.turns,
    setTurns: setCoachChatTurns,
    input: state.input,
    setInput: setCoachChatInput,
    busy: state.busy,
    send: sendCoachChatMessage,
    bottomRef,
  };
}
